/**
 * 提醒弹窗
 * - 监听 useReminderStore, 一次显示队首
 * - 强弱两种样式:
 *   - normal (due_date): 蓝色, 静态, 无动画
 *   - strong (reminder): 橙红色, 呼吸/抖动动画, 强制吸引注意
 * - 三个按钮:
 *   - 已完成: PATCH completed=1
 *   - 推迟: 点开下拉菜单, 默认 5 分钟, 可选 15/30 分钟 / 1 小时 / 明天同时
 *   - 关闭: 仅移除弹窗, 不改服务端
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReminderStore, type ReminderItem } from '../store/reminder';
import { tasksApi, ApiError } from '../api/client';
import { toast } from '../store/toast';
import { haptic } from '../hooks/useHaptics';

/** 推迟选项: { 标签, 毫秒数 } */
interface SnoozeOption {
  label: string;
  ms: number;
}

const SNOOZE_OPTIONS: SnoozeOption[] = [
  { label: '5 分钟', ms: 5 * 60 * 1000 },
  { label: '15 分钟', ms: 15 * 60 * 1000 },
  { label: '30 分钟', ms: 30 * 60 * 1000 },
  { label: '1 小时', ms: 60 * 60 * 1000 },
  { label: '明天同时', ms: -1 }, // 特殊: 不只是 +N 毫秒, 而是"明天这个时间"
];

/** 计算"明天同时"的毫秒数 (24h 后, 跨过午夜) */
function tomorrowSameTime(): number {
  return Date.now() + 24 * 60 * 60 * 1000;
}

/**
 * 把 store 里的队首弹出来展示
 * 注意: 队列里可能有多条 (比如后端一次扫到多个 task), 一次只弹一条,
 * 用户处理完 (或关闭) 后弹出下一条
 */
export function ReminderDialog() {
  const items = useReminderStore((s) => s.items);
  const dismiss = useReminderStore((s) => s.dismiss);
  const current = items[0];

  if (!current) return null;
  return (
    <ReminderModal
      key={current.id}
      item={current}
      onDismiss={() => dismiss(current.id)}
    />
  );
}

function ReminderModal({
  item,
  onDismiss,
}: {
  item: ReminderItem;
  onDismiss: () => void;
}) {
  const navigate = useNavigate();
  const submittingRef = useRef(false);

  // strong 提醒 → 触发浏览器通知 (权限已有的话) + 振动
  useEffect(() => {
    if (item.urgency === 'strong') {
      haptic('medium');
      if (
        'Notification' in window &&
        Notification.permission === 'granted'
      ) {
        try {
          const n = new Notification(`⏰ 提醒: ${item.title}`, {
            body: item.body,
            tag: `task-reminder-${item.task_id}`,
            requireInteraction: true,
          });
          n.onclick = () => {
            window.focus();
            navigate(`/?task=${item.task_id}`);
            n.close();
          };
        } catch {
          /* ignore */
        }
      }
    }
  }, [item.urgency, item.title, item.body, item.task_id, navigate]);

  const handleComplete = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const t = await tasksApi.get(item.task_id);
      await tasksApi.update(item.task_id, {
        completed: 1,
        version: t.version,
      });
      toast({ kind: 'success', text: `已完成: ${item.title}` });
      haptic('light');
      onDismiss();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      toast({ kind: 'error', text: `标记完成失败: ${msg}` });
      submittingRef.current = false;
    }
  };

  const handleSnoozeWith = async (opt: SnoozeOption) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const t = await tasksApi.get(item.task_id);
      const newTime = opt.ms === -1 ? tomorrowSameTime() : Date.now() + opt.ms;
      if (item.kind === 'due_date') {
        // 截止日期到期提醒: 推迟, 重置 due_date_sent
        await tasksApi.update(item.task_id, {
          due_date: newTime,
          due_date_sent: 0,
          version: t.version,
        });
      } else {
        // 提醒时间: 推迟, 重置 reminder_sent
        await tasksApi.update(item.task_id, {
          reminder_at: newTime,
          reminder_sent: 0,
          version: t.version,
        });
      }
      toast({ kind: 'info', text: `已推迟 ${opt.label}` });
      onDismiss();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      toast({ kind: 'error', text: `推迟失败: ${msg}` });
      submittingRef.current = false;
    }
  };

  const handleClose = () => {
    onDismiss();
  };

  const handleOpen = () => {
    onDismiss();
    navigate(`/?task=${item.task_id}`);
  };

  const isStrong = item.urgency === 'strong';

  return (
    <>
      {/* 背景遮罩 */}
      <div
        className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      {/* 弹窗本体 */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="reminder-title"
        className={`fixed left-1/2 top-1/2 z-[201] w-[min(420px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 overflow-visible rounded-xl bg-white shadow-2xl ring-1 dark:bg-gray-800 ${
          isStrong
            ? 'ring-2 ring-amber-400 dark:ring-amber-500 animate-[reminderPulse_1.5s_ease-in-out_infinite]'
            : 'ring-gray-200 dark:ring-gray-700'
        }`}
      >
        {/* 顶部色条 */}
        <div
          className={`h-1.5 rounded-t-xl ${
            isStrong
              ? 'bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400'
              : 'bg-gradient-to-r from-blue-400 via-sky-500 to-blue-400'
          }`}
        />
        <div className="p-5">
          {/* 标签 */}
          <div className="mb-2 flex items-center gap-2 text-xs">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
                isStrong
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                  : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
              }`}
            >
              {isStrong ? '⏰ 提醒' : '📅 已到期'}
            </span>
          </div>
          {/* 标题 */}
          <h2
            id="reminder-title"
            className="text-lg font-semibold leading-snug text-gray-900 dark:text-gray-100"
          >
            {item.title}
          </h2>
          {/* 正文 */}
          {item.body && (
            <p className="mt-1.5 line-clamp-3 text-sm text-gray-600 dark:text-gray-400">
              {item.body}
            </p>
          )}
          {/* 操作按钮: 已完成 + 关闭 (主操作) */}
          <div className="mt-5 flex gap-2">
            <button
              onClick={handleComplete}
              className="flex-1 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 active:scale-[0.98]"
            >
              ✓ 已完成
            </button>
            <button
              onClick={handleClose}
              className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-[0.98] dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            >
              关闭
            </button>
          </div>

          {/* 推迟时长 chips: 一行平铺, 点哪个就推迟多久, 默认 5 分钟高亮 */}
          <div className="mt-3">
            <div className="mb-1.5 text-xs text-gray-400">推迟 (默认 5 分钟)</div>
            <div className="flex flex-wrap gap-1.5">
              {SNOOZE_OPTIONS.map((opt, i) => {
                const isDefault = i === 0;
                return (
                  <button
                    key={opt.label}
                    onClick={() => handleSnoozeWith(opt)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition active:scale-[0.97] ${
                      isDefault
                        ? isStrong
                          ? 'bg-amber-500 text-white shadow-sm hover:bg-amber-600'
                          : 'bg-blue-500 text-white shadow-sm hover:bg-blue-600'
                        : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* 辅助操作: 查看任务详情 */}
          <button
            onClick={handleOpen}
            className="mt-3 block w-full text-center text-xs text-gray-500 underline-offset-2 hover:underline dark:text-gray-400"
          >
            打开任务详情
          </button>
        </div>
      </div>
    </>
  );
}